import { useState } from 'react';
import type { FormEvent, ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { SCREEN_PATHS } from '@/shared/routes';
import { Alert } from '@/shared/ui/Alert';
import { Button } from '@/shared/ui/Button';
import { SecretField } from '@/shared/ui/Field';
import { PageHeader } from '@/shared/ui/PageHeader';
import { Card } from '@/shared/ui/Card';
import { Sections } from '@/shared/ui/Layout';

import { useChangePassword } from '../api/auth';
import { MIN_PASSWORD_LENGTH } from '../model/reset';

/* The screen somebody lands on when their password was chosen for them.
 *
 * **This is the half that was missing, and its absence was the whole defect.** The server refuses
 * every other route while the obligation stands, and that refusal was reaching the reader as the raw
 * problem detail on whichever screen they happened to open — *"Send the current password and a new
 * one to POST /v1/auth/password"* — under a heading saying rules could not be loaded. A person who
 * has never seen an HTTP verb was being handed one and told the platform was broken. Enforcement
 * without a destination is not a feature; it is an error message.
 *
 * So the reader is *brought here* by `RequirePassword` before they can see anything else, and the
 * page explains why in the terms they have: somebody else set this password, so it has to be
 * replaced before the account is theirs alone.
 *
 * There is deliberately no way to skip it and no navigation around it. A "later" control would leave
 * an account whose password is known by two people, which is the state this exists to end — and
 * every other screen would refuse anyway, so the control would be a lie.
 */

export function ChangePasswordForm(): ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const change = useChangePassword();

  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [attempted, setAttempted] = useState(false);
  const [resetKey, setResetKey] = useState(0);

  const tooShort = next.length > 0 && next.length < MIN_PASSWORD_LENGTH;
  /* Compared only once both have something in them, so the mismatch does not appear while somebody
     is still typing the second one — a warning that is always on while you work is one you learn to
     ignore. */
  const mismatched = confirm.length > 0 && next !== confirm;
  /* Refused here as well as by the server. The server's message is about *its* rule; this one can
     point at the field, and a round trip to be told something the page already knows is a round
     trip that makes the platform feel slow. */
  const unchanged = next.length > 0 && next === current;

  const submittable =
    current.length > 0 &&
    next.length >= MIN_PASSWORD_LENGTH &&
    next === confirm &&
    next !== current;

  const onSubmit = (event: FormEvent): void => {
    event.preventDefault();
    setAttempted(true);
    if (!submittable) return;
    change.mutate(
      { current_password: current, new_password: next },
      {
        onSuccess: () => {
          /* Cleared before navigating: these three values are the old password and the new one, and
             leaving them in component state keeps them in memory for as long as the tab lives. */
          setCurrent('');
          setNext('');
          setConfirm('');
          setResetKey((generation) => generation + 1);
          /* `replace`, so Back cannot return to a form whose obligation is already discharged — it
             would render, refuse on submit, and read as a fault. */
          navigate(SCREEN_PATHS.home, { replace: true });
        },
      },
    );
  };

  return (
    <Sections>
      <PageHeader title={t('changePassword.title')} lede={t('changePassword.lede')} />
      <Card title={t('changePassword.cardTitle')} hint={t('changePassword.cardHint')}>
        <form onSubmit={onSubmit}>
          {/* **"The password you were given", not "current password".** The reader did not choose
              it and may be reading it out of an email as they type, so the label names where it came
              from rather than assuming they think of it as theirs. */}
          <SecretField
            label={t('changePassword.field.current')}
            onChange={setCurrent}
            hint={t('changePassword.field.currentHint')}
            required
            resetKey={resetKey}
          />
          <SecretField
            label={t('changePassword.field.next')}
            onChange={setNext}
            hint={t('changePassword.field.nextHint', { minLength: MIN_PASSWORD_LENGTH })}
            required
            resetKey={resetKey}
          />
          <SecretField
            label={t('changePassword.field.confirm')}
            onChange={setConfirm}
            required
            resetKey={resetKey}
          />

          {/* Each problem gets its own sentence pointing at its own field. One combined "check your
              input" would make the reader diff three boxes themselves. */}
          {attempted && current.length === 0 && (
            <Alert tone="warning">{t('changePassword.problem.currentRequired')}</Alert>
          )}
          {tooShort && (
            <Alert tone="warning">
              {t('changePassword.problem.tooShort', { minLength: MIN_PASSWORD_LENGTH })}
            </Alert>
          )}
          {mismatched && (
            <Alert tone="warning">{t('changePassword.problem.mismatch')}</Alert>
          )}
          {unchanged && (
            <Alert tone="warning">{t('changePassword.problem.unchanged')}</Alert>
          )}

          {/* The server's own refusal, verbatim — it is the only one that knows whether the old
              password was right, and it deliberately does not say which of the two was wrong. */}
          {change.isError && (
            <Alert tone="error" title={t('changePassword.refusedTitle')}>
              {change.error.message}
            </Alert>
          )}

          <div className="lq-form-actions">
            {/* No Cancel and no "skip for now": every other route refuses this token, so a way out
                of this page would lead only to screens that fail. */}
            <Button type="submit" tone="primary" busy={change.isPending}>
              {t('changePassword.submit')}
            </Button>
          </div>
        </form>
      </Card>
    </Sections>
  );
}
